import { Code, ContentCopy, ExpandLess, ExpandMore } from '@mui/icons-material'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import {
  InstallationErrorDiagnostic,
  InstallationErrorPayload,
} from 'common/types/kraken-core/Installation'

const terminalStyles = {
  backgroundColor: '#11161d',
  borderRadius: 1,
  color: '#d7dee8',
  fontFamily: 'Menlo, Consolas, monospace',
  fontSize: 12,
  lineHeight: 1.6,
  overflow: 'auto',
  p: 2,
  whiteSpace: 'pre-wrap' as const,
  wordBreak: 'break-word' as const,
}

export interface InstallationErrorOutputProps {
  error: InstallationErrorPayload
}

function DiagnosticCard({ diagnostic }: { diagnostic: InstallationErrorDiagnostic }) {
  const [showSource, setShowSource] = useState(false)
  const hasSource = Boolean(diagnostic.location || diagnostic.snippet?.length)

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            color={diagnostic.severity === 'warning' ? 'warning' : 'error'}
            label={diagnostic.severity === 'warning' ? 'Warning' : 'Error'}
          />
          <Typography variant="subtitle1" fontWeight="bold">
            {diagnostic.summary}
          </Typography>
        </Stack>

        {diagnostic.detail ? <Typography variant="body2">{diagnostic.detail}</Typography> : null}

        {hasSource ? (
          <Box>
            <Button
              size="small"
              endIcon={showSource ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setShowSource(previous => !previous)}
              variant="text"
              color="secondary"
              sx={{ px: 0, minWidth: 0 }}
            >
              Source details
            </Button>
            <Collapse in={showSource}>
              <Stack spacing={1} sx={{ mt: 1 }}>
                {diagnostic.location ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: 'Menlo, Consolas, monospace' }}
                  >
                    {diagnostic.location}
                  </Typography>
                ) : null}
                {diagnostic.snippet?.length ? (
                  <Box sx={terminalStyles}>{diagnostic.snippet.join('\n')}</Box>
                ) : null}
              </Stack>
            </Collapse>
          </Box>
        ) : null}
      </Stack>
    </Paper>
  )
}

export default function InstallationErrorOutput({ error }: InstallationErrorOutputProps) {
  const [showRawOutput, setShowRawOutput] = useState(false)
  const [copied, setCopied] = useState(false)

  const message = error.message || 'Installation failed'
  const diagnostics = error.diagnostics || []
  const rawOutput = error.raw_output || ''

  const copyRawOutput = async () => {
    if (!rawOutput) return
    try {
      await navigator.clipboard.writeText(rawOutput)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  if (diagnostics.length === 0) {
    const isSingleLine = !rawOutput.includes('\n')
    return (
      <Alert severity="error">
        <AlertTitle>{message}</AlertTitle>
        {rawOutput ? isSingleLine ? rawOutput : <Box sx={terminalStyles}>{rawOutput}</Box> : null}
      </Alert>
    )
  }

  return (
    <Stack spacing={2}>
      <Alert severity="error">
        <AlertTitle>{message}</AlertTitle>
        {`${diagnostics.length} problem${diagnostics.length > 1 ? 's' : ''} reported.`}
      </Alert>

      {diagnostics.map((diagnostic, index) => (
        <DiagnosticCard key={`${diagnostic.summary}-${index}`} diagnostic={diagnostic} />
      ))}

      {rawOutput ? (
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              startIcon={<Code />}
              endIcon={showRawOutput ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setShowRawOutput(previous => !previous)}
              color="secondary"
            >
              Technical details
            </Button>
            <Tooltip title={copied ? 'Copied' : 'Copy output'}>
              <IconButton size="small" aria-label="Copy output" onClick={copyRawOutput}>
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <Collapse in={showRawOutput}>
            <Box sx={{ ...terminalStyles, maxHeight: 320, mt: 1 }}>{rawOutput}</Box>
          </Collapse>
        </Box>
      ) : null}
    </Stack>
  )
}
